import { FlexComponentMap } from "components/flexFramework/FlexComponents/typings";
import { Logger } from "bernie-logger";
import * as React from "react";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { ServerSideLogger } from "components/utility/ServerSideLogger";
import { UNSUPPORTED_MODULE } from "config/systemEvents";
import { Component, ComponentProps } from "src/typings/flexFramework/FlexComponent";
import { Localization } from "bernie-l10n";
import { BlossomRendererFactory } from "components/flexFramework/renderer/BlossomRendererFactory";

/**
 * Base class to describe a component that we are rendering in Blossom.
 * This is an adapter pattern over the components given to use from whatever data source (e.g. LPT Composition).
 *
 * This class knows what it is, and knows how to render itself appropriately regardless of where its data comes from.
 * The BlossomRenderer class will take a list of Blossom Components to render an entire page.
 */
export abstract class BlossomComponent {
  public fmId: number;
  public fmTitleId?: number;
  public name: string;
  public type: string;
  public modules: Component[];
  public abstract getProps: () => any;
  public abstract getId: () => string | undefined;
  public abstract shouldRender: () => boolean;
  public abstract getView: () => string;
  public abstract getName: () => string;
  public abstract getModules: () => { [key: string]: Component[] } | null;
  public abstract getBlossomComponents: () => BlossomComponent[];
  public abstract getModel: <T>() => T;

  protected constructor(module: Component) {
    this.name = module.name;
    this.fmId = module.fmId;
    this.fmTitleId = module.fmTitleId;
    this.modules = module.modules;
  }

  public render = (
    flexComponentMap: FlexComponentMap,
    key: string,
    context: ExtendedContextStore,
    l10n: Localization,
    logger: Logger
  ) => {
    if (!this.shouldRender()) {
      return null;
    }

    const component = this.getReactComponent(flexComponentMap);
    if (component === undefined) {
      this.logUnsupportedModule(logger, context);

      return null;
    }

    const props: ComponentProps = {
      ...this.getProps(),
      key,
      context,
      l10n
    };

    return React.createElement(component.codeSplit, props);
  };

  /**
   * Returns a Blossom renderer if this component has modules/children to render
   */
  public getModulesRenderer = () => {
    const modules = this.getModules();
    if (!modules) {
      return undefined;
    }

    return BlossomRendererFactory.get(modules);
  };

  /**
   * Gets the react component from the Flex Component Map using its name and view.
   * The convention is: <module name>_<view>
   *
   * e.g. "hotels_travellers-loved"
   *
   * @param flexComponentMap
   */
  public getReactComponent = (flexComponentMap: FlexComponentMap) => {
    const flexComponentName = this.getName();
    const view = this.getView();

    // If no view, just get using the name
    if (!view) {
      return flexComponentMap.get(flexComponentName);
    }

    // If there is a view, let's try to get it
    const component = flexComponentMap.get(`${flexComponentName}_${view}`);
    if (view && component) {
      return component;
    }

    // Default
    return flexComponentMap.get(flexComponentName);
  };

  /**
   * Takes the name of a Flex component found in a composition that had no corresponding Flex UI Component, and reports it to the server side logger
   */
  private logUnsupportedModule = (logger: Logger, context: ExtendedContextStore) => {
    const contextualInfo = Object.entries({
      pageType: context && context.searchContext && context.searchContext.pageType,
      deviceId: context && context.deviceId,
      userAgent: context && context.userAgent,
      langId: context && context.langId,
      locale: context && context.locale,
      siteId: context && context.site && context.site.id,
      requestId: context && context.traceInfo && context.traceInfo["Trace-ID"]
    })
      .map((key, value) => `${key}=\"${value}\"`)
      .toString();

    ServerSideLogger(logger!).logEvent(
      UNSUPPORTED_MODULE,
      `moduleName=${this.name} is not currently supported by this view layer application`,
      contextualInfo
    );
  };
}
